// swapRoutes.ts

import type { PoolLiquidity } from "./monitor-api"

export interface GraphEdge {
  token: string
  pool: PoolLiquidity
}

export type Graph = { [token: string]: GraphEdge[] }

export interface SwapRoute {
  amount_out: string
  path: string[]
  pool_addresses: string[]
  amounts: string[]
  weight: number
}

export interface SwapRouteResult {
  dijkstra_route: {
    pool_addresses: string[]
    amounts: string[]
    distance: number
    path: string[]
    amount_out: string
  }
  all_routes: SwapRoute[]
}

/* Calculate output amount using the constant product formula with fee */
export function getOutputAmount(
  inputAmount: string,
  pool: PoolLiquidity,
  fromToken: string,
): string {
  if (!inputAmount || inputAmount === "0") return "0"

  let reserveIn: string, reserveOut: string
  if (pool.amm_token0 === fromToken) {
    reserveIn = pool.reserves_0
    reserveOut = pool.reserves_1
  } else if (pool.amm_token1 === fromToken) {
    reserveIn = pool.reserves_1
    reserveOut = pool.reserves_0
  } else {
    return "0"
  }
  if (reserveIn === "0" || reserveOut === "0") return "0"

  const inputB = BigInt(inputAmount)
  const reserveInB = BigInt(reserveIn)
  const reserveOutB = BigInt(reserveOut)
  const feeBps = BigInt(pool.pool_fee_bps || 0)
  const feeMultiplier = 10000n - feeBps
  const amountInWithFee = (inputB * feeMultiplier) / 10000n
  const denominator = reserveInB + amountInWithFee
  if (denominator === 0n) return "0"
  const output = (amountInWithFee * reserveOutB) / denominator
  return output.toString()
}

/* Build a graph from Pools */
export function buildGraph(pools: PoolLiquidity[]): Graph {
  const graph: Graph = {}
  pools.forEach((pool) => {
    if (!graph[pool.amm_token0]) graph[pool.amm_token0] = []
    if (!graph[pool.amm_token1]) graph[pool.amm_token1] = []
    graph[pool.amm_token0].push({ token: pool.amm_token1, pool })
    graph[pool.amm_token1].push({ token: pool.amm_token0, pool })
  })
  return graph
}

/* Calculate edge weight as a number (using output amount scaled by decimals) */
export function calculateEdgeWeight(
  inputAmount: string,
  pool: PoolLiquidity,
  fromToken: string,
): number {
  const output = getOutputAmount(inputAmount, pool, fromToken)
  if (output === "0") return Infinity
  const decimalsOut = pool.amm_token0 === fromToken ? pool.token_1_decimals : pool.token_0_decimals
  return Number(output) / Math.pow(10, decimalsOut)
}

/* Recursively find all token paths (max length = 4) */
function findAllPaths(
  graph: Graph,
  currentPath: string[],
  target: string,
  maxLength: number,
): string[][] {
  const current = currentPath[currentPath.length - 1]
  if (current === target) return [currentPath]
  if (currentPath.length >= maxLength) return []
  let paths: string[][] = []
  for (const edge of graph[current] || []) {
    if (!currentPath.includes(edge.token)) {
      const newPath = [...currentPath, edge.token]
      paths = paths.concat(findAllPaths(graph, newPath, target, maxLength))
    }
  }
  return paths
}

/* For a given token path, try all pool combinations along the path */
function processPoolCombination(
  graph: Graph,
  path: string[],
  index: number,
  currentAmount: string,
  totalWeight: number,
  poolAddresses: string[],
  amounts: string[],
): SwapRoute[] {
  if (index >= path.length - 1) {
    return [
      {
        amount_out: currentAmount,
        path,
        pool_addresses: poolAddresses,
        amounts,
        weight: totalWeight,
      },
    ]
  }
  const from = path[index],
    to = path[index + 1]
  const edges = (graph[from] || []).filter((e) => e.token === to)
  // Sort edges by best output descending
  edges.sort((a, b) => {
    const aOut = getOutputAmount(currentAmount, a.pool, from)
    const bOut = getOutputAmount(currentAmount, b.pool, from)
    return BigInt(bOut) > BigInt(aOut) ? 1 : BigInt(bOut) === BigInt(aOut) ? 0 : -1
  })
  let routes: SwapRoute[] = []
  for (const edge of edges) {
    const newAmt = getOutputAmount(currentAmount, edge.pool, from)
    if (newAmt === "0") continue
    const newWeight = totalWeight + calculateEdgeWeight(currentAmount, edge.pool, from)
    const newPools = [...poolAddresses, edge.pool.amm_process]
    const newAmounts = [...amounts, newAmt]
    routes = routes.concat(
      processPoolCombination(graph, path, index + 1, newAmt, newWeight, newPools, newAmounts),
    )
  }
  return routes
}

/* Get all swap routes from baseToken to quoteToken */
export function getAllRoutes(
  graph: Graph,
  baseToken: string,
  quoteToken: string,
  amountIn: string,
): SwapRoute[] {
  const MAX_PATH_LENGTH = 4
  const tokenPaths = findAllPaths(graph, [baseToken], quoteToken, MAX_PATH_LENGTH)
  const routes: SwapRoute[] = []
  const seen = new Set<string>()

  function routeKey(path: string[], pools: string[]): string {
    return path.join("-") + "|" + pools.join("-")
  }

  for (const p of tokenPaths) {
    const opts = processPoolCombination(graph, p, 0, amountIn, 0, [], [amountIn])
    for (const route of opts) {
      const key = routeKey(route.path, route.pool_addresses)
      if (!seen.has(key)) {
        seen.add(key)
        routes.push(route)
      }
    }
  }
  // Sort by highest output amount
  routes.sort((a, b) => (BigInt(b.amount_out) > BigInt(a.amount_out) ? 1 : -1))
  return routes
}

/* Calculate best output for a given path by selecting optimal pools */
export function calculatePathAmount(
  graph: Graph,
  path: string[],
  amountIn: string,
): { amounts: string[]; amountOut: string; poolAddresses: string[] } {
  let current = amountIn
  const pools: string[] = []
  const amounts: string[] = [current]
  for (let i = 0; i < path.length - 1; i++) {
    const from = path[i],
      to = path[i + 1]
    let bestAmt = "0"
    let bestPool: string | undefined
    for (const edge of graph[from] || []) {
      if (edge.token === to) {
        const out = getOutputAmount(current, edge.pool, from)
        if (BigInt(out) > BigInt(bestAmt)) {
          bestAmt = out
          bestPool = edge.pool.amm_process
        }
      }
    }
    if (bestPool) {
      current = bestAmt
      pools.push(bestPool)
      amounts.push(current)
    }
  }
  return { amounts, amountOut: current, poolAddresses: pools }
}

/* Find best path via a dynamic programming (Bellman-Ford style) approach */
export function findBestPath(
  graph: Graph,
  startToken: string,
  endToken: string,
  amountIn: string,
): { path: string[]; amountOut: string; poolAddresses: string[] } {
  const MAX_PATH_LENGTH = 4
  const best: { [token: string]: string } = {}
  const prev: { [token: string]: { from: string; pool: PoolLiquidity } | null } = {}
  const pathLen: { [token: string]: number } = {}

  for (const token in graph) {
    best[token] = "0"
    pathLen[token] = 0
  }
  best[startToken] = amountIn
  pathLen[startToken] = 1

  for (let i = 1; i < MAX_PATH_LENGTH; i++) {
    for (const token in graph) {
      if (best[token] !== "0") {
        for (const edge of graph[token] || []) {
          if (pathLen[token] < MAX_PATH_LENGTH) {
            const candidate = getOutputAmount(best[token], edge.pool, token)
            if (BigInt(candidate) > BigInt(best[edge.token] || "0")) {
              best[edge.token] = candidate
              prev[edge.token] = { from: token, pool: edge.pool }
              pathLen[edge.token] = pathLen[token] + 1
            }
          }
        }
      }
    }
  }

  if (!best[endToken] || best[endToken] === "0") {
    return { path: [], amountOut: "0", poolAddresses: [] }
  }

  const path: string[] = []
  const pools: string[] = []
  let token = endToken
  const visited = new Set<string>()
  while (token) {
    if (visited.has(token)) return { path: [], amountOut: "0", poolAddresses: [] }
    visited.add(token)
    path.unshift(token)
    if (token === startToken) break
    const data = prev[token]
    if (!data) break
    pools.unshift(data.pool.amm_process)
    token = data.from
  }
  return { path, amountOut: best[endToken], poolAddresses: pools }
}

export function getSwapRoutesLocal(
  pools: PoolLiquidity[],
  baseToken: string,
  quoteToken: string,
  amountIn: string,
): SwapRouteResult {
  const graph = buildGraph(pools)
  const all_routes = getAllRoutes(graph, baseToken, quoteToken, amountIn)
  const bestResult = findBestPath(graph, baseToken, quoteToken, amountIn)
  const dijkstra = calculatePathAmount(graph, bestResult.path, amountIn)

  return {
    all_routes,
    dijkstra_route: {
      path: bestResult.path,
      pool_addresses: dijkstra.poolAddresses,
      amounts: dijkstra.amounts,
      distance: bestResult.path.length,
      amount_out: dijkstra.amountOut,
    },
  }
}
